import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasTable } from '../gas-table.namespace';
import { GasIcon } from '../../../utils/gas-icons/icon/gas-icon.namespace';
import { GasSimpleTableComponent } from '../gas-simple-table/gas-simple-table.component';

/**
 * Данный компонент отвечает за настройку колонок в таблице
 */
@Component({
  selector: 'gas-table-column-settings',
  templateUrl: './gas-table-column-settings.component.html',
  styleUrls: ['./gas-table-column-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasTableColumnSettingsComponent implements OnChanges {
  // эммитит значение при клике по кнопке "Применить"
  @Output('applyColumnManage')
  public applyColumnManage$ = new EventEmitter<Record<string, boolean>>();

  // эммитит значение при клике по кнопке "Сбросить/Отменить"
  @Output('resetColumnManage')
  public resetColumnManage$ = new EventEmitter<void>();

  // Заголовок попапа
  @Input()
  public columnsVisibilityPopupTitle = 'Колонки, показанные в таблице';

  // Массив с ключами колонок, которые должны быть скрыты по дефолту
  @Input()
  public hiddenTableColumnsKeys: string[] = [];

  // Таблица
  @Input()
  public simpleTableComponent: GasSimpleTableComponent;

  // Флаг для отображения/скрытия кнопки "Сбросить/отменить"
  @GasInputBoolean()
  @Input()
  public visibleResetColumnButton = false;

  // Список html-элементов для инициальзации настройки управления колонками
  @Input('initColumn')
  public initColumnProperty: QueryList<ElementRef>;

  // Флаг для обновления отображения колонок
  @Input('updateColumnsVisibility')
  public updateColumnsVisibilityProperty: boolean;

  // Иконки для попапа
  public readonly columnsConfigActionIcon = GasIcon.Core.Settings;
  public readonly columnsConfigExpandIcon = GasIcon.Arrows.ChevronDown;

  // Системная переменая для закрытия попапа
  public closeSubject$ = new Subject<void>();
  // Массив колонок
  public columnsCheckboxes$ = new BehaviorSubject<GasTable.TableColumnVisibilitySettings[]>([]);

  // Массив с актуальными значениями колонок
  private currentCheckboxes: GasTable.TableColumnVisibilitySettings[] = [];
  // Сабжект с состоянием подтверждения настроек
  private applyColumnSettings$ = new BehaviorSubject<boolean>(false);

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  /**
   * Следит за изменением переменных для обновления отображения колонок
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.initColumnProperty?.currentValue?.length > 0) {
      this.initColumnManage(changes.initColumnProperty.currentValue);
    }
    if (changes.updateColumnsVisibilityProperty?.currentValue !== changes.updateColumnsVisibilityProperty?.previousValue) {
      this.updateColumnsVisibility();
    }
  }

  /**
   * @param list - список html-элементов которые требуются для инициализации колонок
   * Метод пробегается по списку, создает для каждого объект с настройками и все помещает в сабжект this.columnsCheckboxes$
   * после чего вызывает метод updateCurrentColumnVisibility для отрисовки таблицы
   */
  public initColumnManage(list: QueryList<ElementRef>): void {
    if (list.first.nativeElement.children instanceof HTMLCollection) {
      this.columnsCheckboxes$.next([]);

      Array.from(list.first.nativeElement.children).forEach((th: HTMLElement, index: number) => {
        const attribute = th.getAttribute('tableColumnKey') || th.getAttribute('key');

        if (attribute) {
          const checkbox: GasTable.TableColumnVisibilitySettings = {
            key: attribute,
            index,
            label: th.textContent,
            checked: !this.hiddenTableColumnsKeys.includes(attribute),
          };

          this.columnsCheckboxes$.next([...this.columnsCheckboxes$.getValue(), ...[checkbox]]);

          this.updateCurrentColumnVisibility(checkbox.index, checkbox.checked);
        }
      });

      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Открытие/закрытие поповера
   * При закрытии окна с настройками колонок проверяем, были ли приянты изменения
   * если нет - то нужно поменять значения чекбоксов, которые пользователь кликал
   * на противоположные значения
   */
  public togglePopover(opened: boolean): void {
    if (!this.applyColumnSettings$.value && !opened) {
      const currentMap = this.currentCheckboxes
        .reduce((map: Record<string, GasTable.TableColumnVisibilitySettings>, obj: GasTable.TableColumnVisibilitySettings) => {
          map[obj.key] = obj;
          return map;
        }, {});

      this.columnsCheckboxes$.value.forEach((column: GasTable.TableColumnVisibilitySettings) => {
        if (column.key in currentMap) {
          column.checked = !column.checked;
        }
      });

      this.currentCheckboxes = [];
    }
  }

  /**
   * Сохранение настроек. Перебирает выбранные пользователем чекбоксы, вызывает метод updateCurrentColumnVisibility
   * для перерисовки и эммитит наверх значение выбранных колонок
   */
  public applyColumnManage(): void {
    this.currentCheckboxes.forEach((checkbox: GasTable.TableColumnVisibilitySettings) => {
      this.updateCurrentColumnVisibility(checkbox.index, checkbox.checked);

      this.applyColumnManage$.emit({
        [checkbox.key]: checkbox.checked,
      });
    });

    this.applyColumnSettings$.next(true);
    this.closeSubject$.next();
    this.currentCheckboxes = [];
  }

  /**
   * Сбрасывает настройки колонок к начальным и эммитит наверх событие
   */
  public resetColumnManage(): void {
    this.resetColumnManage$.emit();
    this.initColumnManage(this.simpleTableComponent.tableHeadRow);
    this.closeSubject$.next();
  }

  /**
   * @param key - ключ выбранной колонки
   * Меняет значение поля checked для каждого отмеченного чекбокса
   */
  public onChangeColumnManage(key: string): void {
    const currentCheckboxes = [...this.columnsCheckboxes$.getValue()].map((checkbox: GasTable.TableColumnVisibilitySettings) => {
      if (checkbox.key === key) {
        checkbox.checked = !checkbox.checked;
      }

      return checkbox;
    });

    this.columnsCheckboxes$.next(currentCheckboxes);

    const currentCheckbox = this.columnsCheckboxes$.getValue().find((checkbox) => checkbox.key === key);

    this.applyColumnSettings$.next(false);
    this.currentCheckboxes.push(currentCheckbox);
  }

  /**
   * Перебирает колонки и для каждой обновляет представление
   */
  public updateColumnsVisibility(): void {
    this.columnsCheckboxes$.getValue().forEach(key => {
      this.updateCurrentColumnVisibility(key.index, key.checked);
    });
  }

  /**
   * Обновляет представление таблицы путем навешивания соответствующих классов
   */
  public updateCurrentColumnVisibility(index: number, checked: boolean): void {
    const thClasses = this.simpleTableComponent.tableHeadRow.first.nativeElement.children[index].classList;
    if (checked) {
      thClasses.add('visible');
    } else {
      thClasses.remove('visible');
    }

    this.simpleTableComponent.tableBodyRows.forEach(tableBodyRow => {
      const tdClasses = tableBodyRow.nativeElement.children[index].classList;
      if (checked) {
        tdClasses.add('visible');
      } else {
        tdClasses.remove('visible');
      }
    });
  }
}
